import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeftRight, Plus } from "lucide-react";
import { useAppContext } from "../store";
import { LANGUAGE_BY_ID } from "../lib/references";
import { track } from "../lib/analytics";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";

/**
 * Two-field quick-add for the "My Words" collection — reachable from the
 * Dashboard's "Мои слова" section and from Drill's post-session screen
 * (`source` tells the two apart in analytics). Saves straight into the
 * lazily-created default deck for the user's active language pair
 * (see lib/words.ts) via `quickAddWord`, so the word shows up in the next
 * Drill batch immediately — no separate "create a deck" step.
 */
export function QuickAddWordDialog({
  open,
  onOpenChange,
  source,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source: "dashboard" | "post_drill";
}) {
  const { t } = useTranslation();
  const { state, quickAddWord } = useAppContext();
  const [word, setWord] = useState("");
  const [translation, setTranslation] = useState("");
  const [reversed, setReversed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addedCount, setAddedCount] = useState(0);

  const learning = state.settings.learningLangs[0];
  const primary = state.settings.primaryLang;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setWord("");
      setTranslation("");
      setAddedCount(0);
    }
    onOpenChange(next);
  };

  if (!learning) return null;

  const wordLang = reversed ? primary : learning;
  const translationLang = reversed ? learning : primary;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!word.trim() || !translation.trim() || saving) return;
    setSaving(true);
    try {
      await quickAddWord(word.trim(), translation.trim(), reversed);
      track("word_added", { source });
      setAddedCount((c) => c + 1);
      setWord("");
      setTranslation("");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t("myWords.addTitle")}</DialogTitle>
          <DialogDescription>{t("myWords.addDesc")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {LANGUAGE_BY_ID[wordLang]?.flag} → {LANGUAGE_BY_ID[translationLang]?.flag}
            </span>
            <button
              type="button"
              onClick={() => setReversed((v) => !v)}
              className="inline-flex min-h-11 min-w-11 items-center gap-1 text-xs font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
            >
              <ArrowLeftRight size={14} /> {t("myWords.swapDirection")}
            </button>
          </div>
          <label className="block text-xs">
            <span className="text-slate-500 dark:text-slate-400 mb-1 block">{t("myWords.wordLabel")}</span>
            <input
              autoFocus
              type="text"
              value={word}
              onChange={(e) => setWord(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100"
            />
          </label>
          <label className="block text-xs">
            <span className="text-slate-500 dark:text-slate-400 mb-1 block">{t("myWords.translationLabel")}</span>
            <input
              type="text"
              value={translation}
              onChange={(e) => setTranslation(e.target.value)}
              className="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100"
            />
          </label>
          {addedCount > 0 && (
            <p className="text-xs text-emerald-600 dark:text-emerald-400" role="status">
              {t("myWords.addedCount", { count: addedCount })}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              onClick={() => handleOpenChange(false)}
              className="px-4 py-2 rounded-xl text-sm font-medium text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              {t("myWords.doneButton")}
            </button>
            <button
              type="submit"
              disabled={!word.trim() || !translation.trim() || saving}
              className="inline-flex items-center justify-center gap-1.5 bg-indigo-600 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
            >
              <Plus size={16} /> {t("myWords.submitButton")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
